import React, { useEffect, useState } from 'react';
import { Checkbox, Menu, MenuItem } from '@mui/material';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import getServices from '../../Services/get-service';
import swal from 'sweetalert';
import more from '../../Assets/images/more.svg';
import Loading from '../SelectValue/Loading';
import Constant from '../../Constant';
import TopDCom from '../Savings.js/TopDCom';
import AssetFinancingRequestDetails from './AssetFinancingRequestDetails';

const statusOptions = ['', 'PENDING', 'APPROVED', 'REJECTED', 'CANCELLED', 'COMPLETED'];

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  return `NGN ${Constant.FormatNumber(value)}`;
};

const formatDate = (value) => {
  if (!value) return 'No date';
  return value.slice(0, 10);
};

const getTotalPayableAmount = (request) => {
  return (
    request.totalAmountPayable ??
    request.totalPayableAmount ??
    request.totalPayable ??
    request.totalDisplayPrice ??
    request.payableAmount ??
    request.totalAmount ??
    null
  );
};

const formatStatusLabel = (status = '') => {
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const getStatusClass = (status = '') => {
  switch (status.toUpperCase()) {
    case 'APPROVED':
    case 'COMPLETED':
      return 'bg-green-100 text-green-800';
    case 'REJECTED':
    case 'CANCELLED':
      return 'bg-red-100 text-red-800';
    case 'PENDING':
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex rounded-[32px] px-3 py-2 text-sm font-medium ${getStatusClass(status)}`}>
    {formatStatusLabel(status || 'PENDING')}
  </span>
);

export default function AssetFinancingMgt() {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [assetStats, setAssetStats] = useState(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [reviewRequestId, setReviewRequestId] = useState(null);

  const fetchRequests = () => {
    setLoading(true);
    getServices.getAssetFinancingRequests({
      status,
      page,
      limit: rowsPerPage,
      q: search,
    }).then(
      (response) => {
        setLoading(false);
        if (response.data.status === '2000') {
          setRequests(response.data.data?.requests || []);
          setPagination(response.data.data?.pagination || null);
        } else {
          swal(response.data.description || 'Failed to fetch asset financing requests');
        }
      },
      (error) => {
        setLoading(false);
        swal(error?.response?.data?.description || 'Error fetching asset financing requests');
      }
    );
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, status]);

  useEffect(() => {
    getServices.getAssetProductStats().then(
      (response) => {
        if (response.data.status === '2000') {
          setAssetStats(response.data.data || null);
        } else {
          swal(response.data.description || 'Failed to fetch asset stats');
        }
      },
      (error) => {
        swal(error?.response?.data?.description || 'Error fetching asset stats');
      }
    );
  }, []);

  const handleOpenMenu = (event, requestId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRequestId(requestId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRequestId(null);
  };

  const viewRequest = (requestId) => {
    handleCloseMenu();
    setReviewRequestId(requestId);
  };

  const handlePreviousPage = () => {
    if (pagination?.hasPrevious || page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination?.hasNext) {
      setPage(page + 1);
    }
  };

  const totalPages = pagination?.totalPages || 1;
  const currentPage = pagination?.currentPage ?? page;

  return (
    <>
      <Loading open={loading} />
      <AssetFinancingRequestDetails
        open={Boolean(reviewRequestId)}
        requestId={reviewRequestId}
        onClose={() => setReviewRequestId(null)}
        onReviewed={fetchRequests}
      />
      <div className="my-8 flex flex-wrap items-center gap-4">
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="w-full sm:w-auto lg:col-span-2">
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Search user by name or email address"
              className="border border-gray-300 focus:border-primary-color outline-none p-3 rounded w-full"
            />
          </div>
          <div className="w-full sm:w-auto">
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
              className="border w-full p-3 rounded bg-white"
            >
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {option ? formatStatusLabel(option) : 'All requests'}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 md:grid-cols-5 mb-10 gap-4">
        <TopDCom name="Total assets in inventory" count={assetStats?.totalAssetsInInventory ?? 0} />
        <TopDCom name="Assets sold" count={assetStats?.assetsSold ?? 0} />
        <TopDCom name="Assets amount sold" count={`NGN ${Constant.FormatNumber(assetStats?.assetsAmountSold || 0)}`} />
        <TopDCom name="Assets amount paid" count={`NGN ${Constant.FormatNumber(assetStats?.assetsAmountPaid || 0)}`} />
        <TopDCom name="Assets amount outstanding" count={`NGN ${Constant.FormatNumber(assetStats?.assetsAmountOutstanding || 0)}`} />
      </div>

      <div className="mt-16">
        <div className="hidden md:flex flex-row justify-between items-center bg-primary-bg rounded-t-md rounded-r-md border-primary-border border p-4">
          <Checkbox />
          <p className="text-text flex-1 text-center whitespace-normal break-words">User</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Asset name</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Down payment / Total payable</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Payment period</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Status</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Action</p>
        </div>
        {loading ? (
          <div>
            {[...Array(rowsPerPage)].map((_, index) => (
              <div
                key={index}
                className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white border-b border-gray-200 p-4 gap-2"
              >
                <div className="w-6 md:w-[5%]">
                  <Skeleton width={24} height={24} />
                </div>
                {[...Array(6)].map((__, skeletonIndex) => (
                  <div key={skeletonIndex} className="flex-1 text-left md:text-center">
                    <Skeleton width="70%" height={20} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center p-4 text-gray-500">
            No asset financing requests found.
          </div>
        ) : (
          <>
            <div className="md:hidden">
              {requests.map((row) => {
                const totalPayableAmount = getTotalPayableAmount(row);

                return (
                  <div
                    key={row.requestId}
                    className="border rounded-lg p-4 mb-3 bg-white shadow-sm"
                  >
                    <p className="font-semibold capitalize">
                      {row.firstName} {row.lastName}
                    </p>
                    <p className="text-sm text-gray-500 break-words">{row.email}</p>
                    <p><b>Asset name:</b> {row.assetName || 'N/A'}</p>
                    <p><b>Date applied:</b> {formatDate(row.createdAt)}</p>
                    <p><b>Downpayment:</b> {formatCurrency(row.downPayment)}</p>
                    <p><b>Total payable:</b> {formatCurrency(totalPayableAmount)}</p>
                    <p><b>Payment period:</b> {row.tenureMonth || 0} Months</p>
                    <p className="mt-2"><StatusBadge status={row.status} /></p>
                    <div className="flex justify-end mt-2">
                      <img
                        src={more}
                        alt="More options"
                        className="cursor-pointer"
                        onClick={(e) => handleOpenMenu(e, row.requestId)}
                      />
                    </div>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl) && selectedRequestId === row.requestId}
                      onClose={handleCloseMenu}
                      sx={{ '& .MuiPaper-root': { boxShadow: 'none' } }}
                    >
                      <MenuItem onClick={() => viewRequest(row.requestId)}>View Request</MenuItem>
                    </Menu>
                  </div>
                );
              })}
            </div>

            <div className="hidden md:block">
              {requests.map((row) => {
                const totalPayableAmount = getTotalPayableAmount(row);

                return (
                  <div
                    key={row.requestId}
                    className="flex flex-row justify-between items-center bg-white border-b border-gray-200 p-4"
                  >
                    <Checkbox />
                    <div className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">
                      <p className="font-medium capitalize">
                        {row.firstName} {row.lastName}
                      </p>
                      <p className="text-sm text-gray-500 break-words">{row.email}</p>
                    </div>
                    <p className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">{row.assetName || 'N/A'}</p>
                    <p className="flex-1 text-center text-primary-color break-words whitespace-normal min-w-0 px-1">
                      {formatCurrency(row.downPayment)} / {formatCurrency(totalPayableAmount)}
                    </p>
                    <p className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">{row.tenureMonth || 0} Months</p>
                    <p className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">
                      <StatusBadge status={row.status} />
                    </p>
                    <div className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">
                      <img
                        src={more}
                        alt="More options"
                        className="mx-auto cursor-pointer"
                        onClick={(e) => handleOpenMenu(e, row.requestId)}
                      />
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && selectedRequestId === row.requestId}
                        onClose={handleCloseMenu}
                        sx={{ '& .MuiPaper-root': { boxShadow: 'none' } }}
                      >
                        <MenuItem onClick={() => viewRequest(row.requestId)}>View Request</MenuItem>
                      </Menu>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-8 mb-10">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 0}
            className={`px-4 py-2 rounded-lg border ${
              currentPage === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-text hover:border-primary-color'
            }`}
          >
            Previous
          </button>
          <p className="text-text text-sm sm:text-base">
            Page {currentPage + 1} of {totalPages}
          </p>
          <button
            onClick={handleNextPage}
            disabled={!pagination?.hasNext}
            className={`px-4 py-2 rounded-lg border ${
              !pagination?.hasNext
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-text hover:border-primary-color'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}
